import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { FormDialog, Field, TextInput, SelectInput, ActionButton } from "./common";

export const JOB_STATUSES = ["Active", "Completed", "Under Review", "Scheduled", "Assigned to Service Provider", "Paused", "Cancelled"];
export const PRIORITIES = ["Normal", "New", "High"];
export const APPROVAL_STATUSES = ["Not Approved", "Approved"];
export const ACTIVITIES = ["Production", "Delivery", "Haul to Disposal", "Disposal", "Pickup", "Service"];
export const ITEMS = ["Produced Water", "Flowback Water"];

const SECTIONS = [
  { title: "Job", fields: [
    { key: "priority", label: "Priority", options: PRIORITIES },
    { key: "job_status", label: "Job Status", options: JOB_STATUSES },
    { key: "approval_status", label: "Approval Status", options: APPROVAL_STATUSES },
    { key: "project", label: "Project" },
    { key: "batch_number", label: "Batch #" },
    { key: "cost_center", label: "Cost Center" },
    { key: "tags", label: "Tags" },
  ]},
  { title: "People", fields: [
    { key: "operator", label: "Operator" },
    { key: "service_provider", label: "Service Provider", dynamic: "asset_locations" },
    { key: "requester", label: "Requester" },
  ]},
  { title: "Resources", fields: [
    { key: "resource_type", label: "Resource Type (lookup)", dynamic: "departments" },
    { key: "activity", label: "Activity", options: ACTIVITIES },
    { key: "resource", label: "Resource (lookup)", dynamic: "assets" },
    { key: "item", label: "Item", options: ITEMS },
    { key: "qty", label: "Qty", type: "number" },
  ]},
  { title: "Schedule & Route", fields: [
    { key: "start", label: "Start", type: "datetime-local" },
    { key: "end", label: "End", type: "datetime-local" },
    { key: "pickup_location", label: "Pickup Location" },
    { key: "destination", label: "Destination" },
    { key: "confirmed_destination", label: "Confirmed Destination" },
    { key: "active_duration", label: "Active Duration" },
    { key: "activated", label: "Activated", type: "datetime-local" },
    { key: "completed", label: "Completed", type: "datetime-local" },
  ]},
  { title: "Details", fields: [
    { key: "details", label: "Details" },
  ]},
];

const NUMERIC_FIELDS = ["qty"];
export const TICKET_KEYS = ["division", "driver", ...SECTIONS.flatMap((s) => s.fields.map((f) => f.key))];

export const JobTicketForm = ({ open, onOpenChange, onSaved, editing = null, defaultStatus = "Active" }) => {
  const [customers, setCustomers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      const f = {};
      TICKET_KEYS.forEach((k) => {
        f[k] = editing[k] != null ? editing[k] : "";
      });
      setForm(f);
    } else {
      setForm(
        Object.fromEntries(
          TICKET_KEYS.map((k) => [k, k === "job_status" ? defaultStatus : k === "approval_status" ? "Not Approved" : ""])
        )
      );
    }
  }, [open, editing, defaultStatus]);

  const dynLists = useMemo(() => ({
    departments: [...new Set(assets.map((a) => a.department).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    assets: assets.map((a) => a.asset_name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    asset_locations: [...new Set(assets.map((a) => a.location).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
  }), [assets]);

  const customerOptions = useMemo(
    () => [...customers].sort((a, b) => a.name.localeCompare(b.name)).map((c) => ({ value: c.id, label: c.name })),
    [customers]
  );
  const driverOptions = useMemo(() => drivers.map((d) => d.full_name).filter(Boolean)
    .sort((a, b) => a.localeCompare(b)).map((name) => ({ value: name, label: name })), [drivers]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {};
    TICKET_KEYS.forEach((k) => {
      const v = form[k];
      if (v === "" || v == null) return;
      payload[k] = NUMERIC_FIELDS.includes(k) ? Number(v) : v;
    });
    try {
      if (editing) {
        await api.patch(`/swd/job-tickets/${editing.id}`, payload);
        toast.success(`Ticket ${editing.ticket_number} updated`);
      } else {
        const r = await api.post("/swd/job-tickets", payload);
        toast.success(`Job ticket ${r.data.ticket_number} created (${r.data.job_status})`);
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title={editing ? `Edit ${editing.ticket_number}` : "New Job Ticket"} testId="swd-ticket-dialog">
      <form onSubmit={submit} className="space-y-6">
        <Field label="Division (Customer)">
          <SelectInput testId="ticket-division-select" value={form.division || ""} onChange={(e) => setForm({ ...form, division: e.target.value })} placeholder="Select customer" options={customerOptions} />
        </Field>
        <Field label="Driver">
          <SelectInput
            testId="ticket-driver-select"
            value={form.driver || ""}
            onChange={(e) => setForm({ ...form, driver: e.target.value })}
            placeholder="Select driver"
            options={driverOptions}
          />
        </Field>
        {SECTIONS.map((s) => (
          <div key={s.title}>
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-500/80 border-b border-slate-800 pb-1 mb-3">{s.title}</p>
            <div className="grid grid-cols-2 gap-4">
              {s.fields.map((f) => {
                const opts = f.options || (f.dynamic ? dynLists[f.dynamic] : null);
                if (opts) {
                  return (
                    <Field key={f.key} label={f.label}>
                      <SelectInput
                        testId={`ticket-field-${f.key}`}
                        value={form[f.key] || ""}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        placeholder="Select…"
                        options={opts.map((o) => ({ value: o, label: o }))}
                      />
                    </Field>
                  );
                }
                return (
                  <Field key={f.key} label={f.label}>
                    <TextInput
                      testId={`ticket-field-${f.key}`}
                      type={f.type || "text"}
                      step={f.type === "number" ? "any" : undefined}
                      value={form[f.key] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    />
                  </Field>
                );
              })}
            </div>
          </div>
        ))}
        <ActionButton testId="ticket-submit-button" type="submit" disabled={busy} className="w-full justify-center">
          {busy ? "Saving…" : editing ? "Save Changes" : "Create Ticket"}
        </ActionButton>
      </form>
    </FormDialog>
  );
};
